// @ts-nocheck - MUI 7.x Grid item prop type compatibility
import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  CircularProgress,
  Divider,
  Grid,
} from '@mui/material';
import { Sync as SyncIcon } from '@mui/icons-material';
import { fleetAdminAPI } from '../services/api';

interface FleetClientOption {
  customer_id: string;
  razon_social: string;
}

interface SyncResult {
  status: string;
  users: { created: number; updated: number };
  groups: { created: number };
}

const FleetADSync: React.FC = () => {
  const [clients, setClients] = useState<FleetClientOption[]>([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [clientIdAzure, setClientIdAzure] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<SyncResult | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [endpointUnavailable, setEndpointUnavailable] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info',
  });

  const loadClients = async () => {
    setClientsLoading(true);
    try {
      const data = await fleetAdminAPI.getFleetClients();
      setClients(data);
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 404 || status === 501 || status === 422) {
        setEndpointUnavailable(true);
      } else {
        setEndpointUnavailable(true);
      }
    } finally {
      setClientsLoading(false);
    }
  };

  useEffect(() => {
    loadClients();
  }, []);

  const handleSync = async () => {
    if (!selectedClientId || !tenantId || !clientIdAzure || !clientSecret) {
      setSnackbar({
        open: true,
        message: 'Por favor complete todos los campos requeridos.',
        severity: 'error',
      });
      return;
    }

    setSyncing(true);
    setSyncResult(null);
    setSyncError(null);

    try {
      const result = await fleetAdminAPI.triggerADSync(selectedClientId, {
        tenant_id: tenantId,
        client_id_azure: clientIdAzure,
        client_secret: clientSecret,
      });
      setSyncResult(result);
      setSnackbar({
        open: true,
        message: 'Sincronización completada exitosamente.',
        severity: 'success',
      });
    } catch (error: any) {
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail;
      if (status === 404 || status === 501) {
        setSyncError('El endpoint de sincronización AD no está disponible todavía. Se implementará en una próxima actualización.');
      } else {
        setSyncError(detail || 'Error al sincronizar con Azure AD. Verifique las credenciales e intente de nuevo.');
      }
      setSnackbar({
        open: true,
        message: 'Error en la sincronización.',
        severity: 'error',
      });
    } finally {
      setSyncing(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Fleet - Sincronización Azure AD
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Sincronice usuarios y grupos desde Azure Active Directory (Entra ID) hacia el Fleet Manager.
        </Typography>
      </Box>

      {endpointUnavailable && (
        <Alert severity="info" sx={{ mb: 3 }}>
          El endpoint de clientes fleet aún no está disponible. Puede ingresar las credenciales de prueba de igual manera.
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Configuración de Azure AD
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={2}>
          <Grid xs={12}>
            <FormControl fullWidth>
              <InputLabel id="client-select-label">Cliente</InputLabel>
              <Select
                labelId="client-select-label"
                value={selectedClientId}
                label="Cliente"
                onChange={(e) => setSelectedClientId(e.target.value)}
                disabled={clientsLoading}
              >
                {clientsLoading ? (
                  <MenuItem value="" disabled>
                    Cargando clientes...
                  </MenuItem>
                ) : clients.length === 0 ? (
                  <MenuItem value="" disabled>
                    No hay clientes disponibles
                  </MenuItem>
                ) : (
                  clients.map((c) => (
                    <MenuItem key={c.customer_id} value={c.customer_id}>
                      {c.razon_social || c.customer_id} ({c.customer_id})
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Grid>

          <Grid xs={12}>
            <TextField
              fullWidth
              label="Tenant ID"
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value)}
              placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              helperText="ID del tenant de Azure AD (Directory ID)"
            />
          </Grid>

          <Grid xs={12}>
            <TextField
              fullWidth
              label="Client ID (Azure App)"
              value={clientIdAzure}
              onChange={(e) => setClientIdAzure(e.target.value)}
              placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              helperText="Application (client) ID del registro de la aplicación en Azure"
            />
          </Grid>

          <Grid xs={12}>
            <TextField
              fullWidth
              label="Client Secret"
              type="password"
              value={clientSecret}
              onChange={(e) => setClientSecret(e.target.value)}
              helperText="Secreto del cliente de la aplicación Azure"
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 3 }}>
          <Button
            variant="contained"
            startIcon={syncing ? <CircularProgress size={18} color="inherit" /> : <SyncIcon />}
            onClick={handleSync}
            disabled={syncing || !selectedClientId || !tenantId || !clientIdAzure || !clientSecret}
            size="large"
          >
            {syncing ? 'Sincronizando...' : 'Sincronizar Ahora'}
          </Button>
        </Box>
      </Paper>

      {syncError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {syncError}
        </Alert>
      )}

      {syncResult && syncResult.status === 'success' && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom color="success.main">
            Sincronización Exitosa
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid xs={12} sm={4}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <Typography variant="h3" color="primary">
                  {syncResult.users?.created ?? 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Usuarios Creados
                </Typography>
              </Box>
            </Grid>
            <Grid xs={12} sm={4}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <Typography variant="h3" color="info.main">
                  {syncResult.users?.updated ?? 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Usuarios Actualizados
                </Typography>
              </Box>
            </Grid>
            <Grid xs={12} sm={4}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <Typography variant="h3" color="secondary.main">
                  {syncResult.groups?.created ?? 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Grupos Creados
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default FleetADSync;
